import { Component, Vue } from 'vue-property-decorator';
import { ConfigurationSection } from '@/models/configuration/Section';
import { Configuration } from '@/models/configuration/Configuration';
import { ConfigurationService, QuickEditService, AxDataService } from '@/services';
import { LoggerService } from '@/utils';
import { ApiIdentifers } from '@/config';
import { TreeItem } from '@/models/quickEditWizard/TreeItem';
import { ConfigurationField } from '@/models/configuration/Field';
import { QuickEditModel } from '@/models/quickEdit/QuickEdit';
import { QuickEditSectionModel } from '@/models/quickEdit/QuickEditSection';
import { QuickEditFieldModel } from '@/models/quickEdit/QuickEditField';

import TitleButton from '@/components/general/titlebutton/TitleButton.vue';
import QuickSelectMenu from '@/components/general/quickselectmenu/QuickSelectMenu.vue';
import HeaderBar from '@/components/general/headerbar/HeaderBar.vue';

@Component({
    components: {
        TitleButton,
        QuickSelectMenu,
        HeaderBar,
    },
})
export default class QuickeditWizardComponent extends Vue {
    public epindex: number = 0;
    public epIndizes: number[] = [0];

    public treeItems: TreeItem[] = [];
    public activeItems = [];

    public mainConfiguration: Configuration = new Configuration();
    public machinesConfiguration: Configuration = new Configuration();
    public samplesConfiguration: Configuration = new Configuration();

    public serialNumber: string | undefined = '';
    public searchField: TreeItem = new TreeItem({
        id: 0,
        identifier: 'ef58da8f',
        isSelectedinTree: false,
        name: 'Serial number',
        required: false,
    });
    public errorMessage: string = '';

    private treeIndex = 0;

    public mounted() {
        this.mainConfiguration = this.getMainConfiguration;
        this.mainConfiguration.sections = this.mainConfiguration.sections.filter((s) => s.identifier !== ApiIdentifers.machineOrder);
        this.machinesConfiguration = this.getMachinesConfiguration;
        this.samplesConfiguration = this.getSamplesConfiguration;

        this.treeItems = [...this.addSectionsToTree(this.mainConfiguration), ...this.addSectionsToTree(this.machinesConfiguration), ...this.addSectionsToTree(this.samplesConfiguration)];

        LoggerService.debug(`treeItems: `, this.treeItems);
    }

    get getMainConfiguration() {
        return ConfigurationService.configuration;
    }

    get getMachinesConfiguration() {
        return ConfigurationService.machinesConfiguration;
    }

    get getSamplesConfiguration() {
        return ConfigurationService.samplesConfiguration;
    }

    public onSaveClicked(close: boolean): void {
        LoggerService.debug(`onSaveClicked`);
        this.prepareQuickViewConfiguration();
    }

    public onCancelClicked() {
        LoggerService.debug(`onCancelClicked`);

        this.$router.back();
    }

    public onItemClicked(inItem: TreeItem): void {
        this.checkSelectedItem(inItem);
    }

    public onSerialNumberInput(): void {
        if (this.serialNumber && this.serialNumber.length >= 10) {
            const serialNumber = this.serialNumber;
            this.errorMessage = '';
            AxDataService.FilterCustomersBySerialnumber(serialNumber)
                .then((customer) => {
                    const treeItemCustomer = this.searchTreeItemByIdentifier(ApiIdentifers.customer);
                    if (treeItemCustomer) {
                        treeItemCustomer.isSelectedinTree = true;
                        this.checkSelectedItem(treeItemCustomer);
                    }
                    AxDataService.GetMachineBySerialnumber(serialNumber)
                        .then((machine) => {
                            LoggerService.debug(`machine found for serial number: ${this.serialNumber} `, machine);
                            const treeItemMachine = this.searchTreeItemByIdentifier(machine.machineId);
                            if (treeItemMachine) {
                                treeItemMachine.isSelectedinTree = true;
                                this.checkSelectedItem(treeItemMachine);
                            }
                        })
                        .catch((error) => {
                            LoggerService.warn(error);
                            this.errorMessage = this.$vuetify.lang.t('$vuetify.quickEditWizard.errorNoMachine');
                        });
                })
                .catch((error) => {
                    LoggerService.warn(error);
                    this.errorMessage = this.$vuetify.lang.t('$vuetify.quickEditWizard.errorNoCustomer');
                });
        } else {
            this.errorMessage = '';
        }
    }

    /**
     * Method to fetch translation with fallback
     * @param item ConfigurationSection
     */
    public getIdentifierText(item: TreeItem): string {
        let ret: string = '';
        ret = this.$vuetify.lang.t(`$vuetify.identifiers.${item.identifier}`);
        // fallback to name string if no translation found
        if (ret === undefined || ret.length === 0 || ret === item.identifier || ret.includes('$vuetify.identifiers.')) {
            ret = item.name;
            LoggerService.LogDebug(`getIdentifierText fallback text for: ${item.identifier} --> ${ret}`);
        }
        return ret;
    }

    public getSetValuesCounter(section: ConfigurationSection): number {
        let setValueCount = 0;
        for (const field of section.fields) {
            setValueCount += field.getSetValuesCounter(section.identifier);
        }
        return setValueCount;
    }

    private addSectionsToTree(inConfiguration: Configuration): TreeItem[] {
        const treeItems: TreeItem[] = [];
        inConfiguration.sections.forEach((section) => {
            this.treeIndex++;
            const treeItem = new TreeItem({
                id: this.treeIndex,
                name: section.name,
                identifier: section.identifier,
                isSelectedinTree: false,
                required: false,
            });
            if (section.identifier === ApiIdentifers.general || section.identifier === ApiIdentifers.material) {
                treeItem.isSelectedinTree = true;
                treeItem.required = true;
            }
            treeItem.children = this.addFieldsToTree(section.fields);
            treeItems.push(treeItem);
        });

        return treeItems;
    }

    private addFieldsToTree(inFields: ConfigurationField[]): TreeItem[] {
        const treeItems: TreeItem[] = [];
        inFields.forEach((field) => {
            this.treeIndex++;
            const treeItem = new TreeItem({
                id: this.treeIndex,
                name: field.name,
                identifier: field.identifier,
                isSelectedinTree: field.required,
                required: field.required,
            });
            if (field.fields) {
                treeItem.children = this.addFieldsToTree(field.fields);
            }
            treeItems.push(treeItem);
        });

        return treeItems;
    }

    private checkSelectedItem(inItem: TreeItem, treeItems?: TreeItem[], deep?: number): boolean {
        treeItems = treeItems ? treeItems : this.treeItems;
        deep = deep ? deep : 0;
        let found = false;
        for (const searchItem of treeItems) {
            if (searchItem.identifier === inItem.identifier) {
                if (searchItem.children) {
                    this.checkAllChildren(searchItem);
                }
                found = true;
                break;
            } else if (searchItem.children) {
                const newDeep = deep + 1;
                found = this.checkSelectedItem(inItem, searchItem.children, newDeep);
                if (found) {
                    searchItem.isSelectedinTree = searchItem.children.some((c) => c.isSelectedinTree);
                    break;
                }
            }
        }
        return found;
    }

    private checkAllChildren(inItem: TreeItem): void {
        if (inItem.children) {
            inItem.children.forEach((child) => {
                child.isSelectedinTree = inItem.isSelectedinTree;
                if (child.children) {
                    this.checkAllChildren(child);
                }
            });
        }
    }

    private prepareQuickViewConfiguration(): void {
        const quickEditModel: QuickEditModel = new QuickEditModel();
        quickEditModel.date = new Date().toISOString();
        quickEditModel.formId = crypto ? crypto.randomUUID() : new Date().getTime().toString();
        quickEditModel.name = 'Quick Edit Wizard';
        this.treeItems.forEach((section) => {
            if (section.isSelectedinTree) {
                const newQuickSection = new QuickEditSectionModel();
                newQuickSection.identifier = section.identifier;
                if (section.children) {
                    newQuickSection.fields = this.addQuickEditFields(section.children);
                }
                quickEditModel.sections.push(newQuickSection);
            }
        });

        QuickEditService.quickEditConfigurationsFromWizard = quickEditModel;

        this.$router.replace({
            name: 'quickEdit',
            params: {
                wizardFormId: quickEditModel.formId || '',
                wizardSerialNumber: this.serialNumber || '',
            },
        });
    }

    private addQuickEditFields(inFields: TreeItem[]): QuickEditFieldModel[] {
        const quickFields: QuickEditFieldModel[] = [];
        inFields.forEach((field) => {
            if (field.isSelectedinTree) {
                const newQuickField = new QuickEditFieldModel();
                newQuickField.identifier = field.identifier;
                if (field.children) {
                    newQuickField.fields = this.addQuickEditFields(field.children);
                }
                quickFields.push(newQuickField);
            }
        });

        return quickFields;
    }

    private searchTreeItemByIdentifier(inIdentifier: string, treeItems?: TreeItem[]): TreeItem | undefined {
        let foundItem: TreeItem | undefined;
        treeItems = treeItems ? treeItems : this.treeItems;
        for (const treeItem of treeItems) {
            if (treeItem.identifier === inIdentifier) {
                foundItem = treeItem;
                break;
            } else if (treeItem.children) {
                foundItem = this.searchTreeItemByIdentifier(inIdentifier, treeItem.children);
                if (foundItem) {
                    break;
                }
            }
        }
        return foundItem;
    }
}
